/**
 * Sprint 8 — Stripe live-mode readiness banner (superuser-only).
 *
 * Mount once near the top of the authenticated layout. Polls
 * /api/v1/billing/health on mount. If the caller isn't a superuser the
 * endpoint 401/403s and the service returns null — banner stays hidden.
 *
 * The banner renders when ALL of these are true:
 *   - DEPLOYMENT_MODE === 'saas' (standalone deployments shouldn't bill)
 *   - live_mode_ready === false (something stops real payments)
 *
 * The warnings array spells out exactly what's wrong, straight from the
 * backend. We don't transform it — keeps the message owner-of-truth on
 * the backend side and makes ops debugging easier.
 */
import { useEffect, useState } from 'react'
import { AlertTriangle, X } from 'lucide-react'

import { billingService, type StripeHealth } from '../../services/billing'
import { useAuth } from '../../contexts/AuthContext'

const DISMISS_KEY = 'karyva.stripeHealthBannerDismissedAt'
// Re-show the banner after this many ms even if dismissed — operational
// reminder, not a permanent hide. 24h: long enough to not be naggy,
// short enough that prod misconfig won't sit silently for a week.
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000

export function StripeHealthBanner() {
  const { user } = useAuth()
  const [health, setHealth] = useState<StripeHealth | null>(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    // Only superusers will get a non-null response.
    if (!user?.is_superuser) return
    let cancelled = false

    billingService
      .getStripeHealth()
      .then((data) => {
        if (!cancelled) setHealth(data)
      })
      .catch(() => {
        // Non-fatal — banner just won't render.
      })
    return () => {
      cancelled = true
    }
  }, [user?.is_superuser])

  useEffect(() => {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0)
    if (at && Date.now() - at < DISMISS_TTL_MS) setDismissed(true)
  }, [])

  if (!user?.is_superuser) return null
  if (!health) return null
  if (dismissed) return null
  // Only show in saas mode where Stripe matters. Standalone deployments
  // intentionally have Stripe disabled.
  if (health.deployment_mode !== 'saas') return null
  // All green — nothing to nag about.
  if (health.live_mode_ready) return null

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
    setDismissed(true)
  }

  return (
    <div
      role="alert"
      className="bg-amber-50 border-b-2 border-amber-300 px-6 py-3 flex items-start gap-3"
    >
      <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-900 mb-1">
          Stripe is not configured to take real payments yet
          {health.secret_key_mode === 'test' && (
            <span className="ml-2 text-xs font-medium uppercase tracking-wide bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">
              TEST MODE
            </span>
          )}
        </p>
        <ul className="text-xs text-amber-800 space-y-0.5 list-disc list-inside">
          {health.warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
        <p className="text-xs text-amber-700 mt-2">
          See <code className="font-mono">STRIPE_LIVE_KEYS_CHECKLIST.md</code> at
          the repo root, or run{' '}
          <code className="font-mono">scripts/flip_stripe_to_live.sh</code> on the
          droplet.
        </p>
      </div>
      <button
        onClick={handleDismiss}
        className="text-amber-700 hover:text-amber-900 flex-shrink-0"
        aria-label="Dismiss for 24 hours"
        title="Dismiss for 24 hours"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
